import {
  type LocaleValue,
  TRANSCEND_SUPPORTED_TRANSLATIONS,
} from '@transcend-io/internationalization';
import { invertSafe } from '@transcend-io/type-utils';

/** Mapping of AWS base translation keys to list of browser locales that should use them */
export const INVERTED_TRANSLATE_LOCALE = invertSafe(
  TRANSCEND_SUPPORTED_TRANSLATIONS,
);

/**
 * Get all potential sub-languages from a given ISO 639-1 language code
 *
 * @param langCode - ISO 639-1 format language code
 * @returns Array of potential sub-languages, sorted by most to least specific
 */
const getAllSubLanguages = (langCode: string): string[] =>
  langCode
    .split('-')
    .flatMap((_, i, tags) => tags.slice(0, i + 1).join('-'))
    .reverse();

/**
 * Match preferred language list against a supported languages list
 *
 * @param preferred - Sorted language list in order of most preferable to least preferable
 * @param supported - List of supported languages to match from
 * @returns Preferred languages (including sub-language matches) that match supported languages list
 */
export const matchLanguages = (
  preferred: string[] | readonly string[],
  supported: string[],
): string[] => {
  const matches = new Set<string>();
  const allSupportedLanguages = supported.flatMap(getAllSubLanguages);
  const nearestTranslations = preferred
    .flatMap(getAllSubLanguages)
    .filter((language) => {
      if (!allSupportedLanguages.includes(language)) {
        return false;
      }
      const unique = !matches.has(language);
      if (unique) {
        matches.add(language);
      }
      return unique;
    });
  if (
    nearestTranslations.length > 0 &&
    !supported.includes(nearestTranslations[0])
  ) {
    nearestTranslations[0] = INVERTED_TRANSLATE_LOCALE[
      nearestTranslations[0]
    ].find((lang) => supported.includes(lang)) as LocaleValue;
  }
  return nearestTranslations;
};
